import React from "react";

import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";

import { Card, Icon, Segment } from "semantic-ui-react";
import { getLocale, isoDate } from "../../../../utils/helperFunctions";
import useTheme from "../../../../hooks/useTheme";
import { PoweredByMedalla, PrimaryChip } from "../../../UI/CustomButtons";
import { LightHorizontalDivider } from "../../../UI/CustomElements";
import { AppColumn, AppRow, Wrap } from "../../../UI/Layouts";
import { useResponsive } from "../../../UI/ResponsiveContext";
import { SizedBox } from "../../../UI/Sizes";
import { WorkplaceCard } from "../../../UI/WorkplaceCard";
import AppText from "../../../UI/Texts/AppTexts";
import { useSelector } from "react-redux";
import WorkplaceInsightDetailCard from "../../../UI/WorkplaceInsightCard";
import { ASSETS } from "../../../../hooks/useAssets";
import { withOpacity } from "../../../../utils/helperFunctions";
import { t } from "i18next";

export function WorkplaceSection({ workplaceRef, jobData, workplaceData }) {
  const theme = useTheme();
  const { isMobile } = useResponsive();

  const coreConstants = useSelector((state) => state.core.constants);
  const allWorkfields = coreConstants.workfields ?? [];

  const healthcareWorkplaceDetails =
    workplaceData.workplace ??
    jobData.healthcareWorkplaceDetails ??
    jobData.workplaceDetails;

  if (!healthcareWorkplaceDetails) return <div></div>;

  const insights = workplaceData?.insights ?? [];

  let workplaceContainerWidth = 0;

  if (typeof document !== "undefined") {
    workplaceContainerWidth =
      typeof document !== "undefined" && document.body.clientWidth;

    const workplaceReference =
      typeof document !== "undefined" &&
      document.getElementById("workplace-container");

    if (workplaceReference) {
      workplaceContainerWidth = workplaceReference.clientWidth;
    }
  }

  const newWorkplaceData = {
    ...healthcareWorkplaceDetails,
    logoUrl: healthcareWorkplaceDetails.logoUrl,
    name: healthcareWorkplaceDetails.name_obj,
    nameVerksamhet: healthcareWorkplaceDetails.name_verksamhet,
    orgName: healthcareWorkplaceDetails.org_name,
  };

  const workplaceSectionContent = (
    <div id="workplace-container">
      <AppColumn crossAxisAlignment={"start"}>
        <SizedBox height={theme.spacing.elementPadding} />
        <div
          style={{
            padding: !isMobile && theme.spacing.contentElementPadding,
          }}
        >
          <WorkplaceCard
            workplaceData={newWorkplaceData}
            insightsAssessment={healthcareWorkplaceDetails?.insightsAssessment}
            width={workplaceContainerWidth}
          />
          <SizedBox height={theme.spacing.elementPadding} />
          {healthcareWorkplaceDetails.workfields &&
            healthcareWorkplaceDetails.workfields.length > 0 && (
              <Wrap>
                {healthcareWorkplaceDetails.workfields.map((wf, index) => {
                  const appWorkfield = allWorkfields.find((e) => e.id === wf);
                  const text = appWorkfield?.text[getLocale()] ?? wf;

                  return (
                    <PrimaryChip
                      key={`${wf}_${index}`}
                      text={text}
                      small
                      icon={appWorkfield?.icon}
                      secondary
                    />
                  );
                })}
              </Wrap>
            )}
          {healthcareWorkplaceDetails?.description &&
            healthcareWorkplaceDetails.description.length > 0 && (
              <AppColumn>
                <SizedBox height={theme.spacing.contentElementPadding} />
                <AppText.BodyText
                  margins={{ marginBottom: "0px" }}
                  fontWeight={600}
                >
                  {t("about")}
                </AppText.BodyText>
                <SizedBox height={theme.spacing.elementPadding} />
                <AppText.BodyText margins={{ marginBottom: "0px" }}>
                  {healthcareWorkplaceDetails.description}
                </AppText.BodyText>
              </AppColumn>
            )}
        </div>
        <SizedBox height={isMobile && theme.spacing.elementPadding} />
        {(healthcareWorkplaceDetails?.insightsAssessment?.score ?? 0) > 0 && (
          <AppColumn>
            {isMobile ? (
              <SizedBox height={theme.spacing.contentElementPadding} />
            ) : (
              <LightHorizontalDivider />
            )}
            <div
              style={{
                padding: !isMobile && theme.spacing.contentElementPadding,
              }}
            >
              <WorkplaceInsightDetailCard
                workPlace={healthcareWorkplaceDetails}
                width={workplaceContainerWidth * 0.92}
              />
              <SizedBox height={theme.spacing.contentElementPadding} />
              <AppText.BodyText>
                {t("jobdetail.workplace.insight.comments.title", {
                  count: insights.length,
                })}
              </AppText.BodyText>
            </div>
            <SizedBox height={isMobile && theme.spacing.elementPadding} />
            <div
              style={{
                msOverflowStyle: "none",
                scrollBehavior: "smooth",
                display: "flex",
                overflowX: "auto",
                scrollbarWidth: "none",
                width: workplaceContainerWidth,
              }}
            >
              <div
                style={{
                  display: "flex",
                  width: workplaceContainerWidth * 2.5,
                  gap: theme.spacing.contentElementPadding,
                  marginLeft: !isMobile && theme.spacing.contentElementPadding,
                  marginRight: theme.spacing.contentElementPadding,
                }}
              >
                {insights.map((insight, index) => {
                  return (
                    <div
                      key={insight.id}
                      style={{
                        padding: theme.spacing.elementPadding,
                        margin: 0,
                        maxWidth: "1000px",
                        width: "350px",
                        borderRadius: theme.radius.default,
                        backgroundColor: withOpacity(
                          theme.colors.lightGrey,
                          "0.4"
                        ),
                        border: "1px solid rgba(0, 0, 0, 0.1)",
                        boxShadow: "0 0px 0px rgba(0, 0, 0, 0.2)",
                      }}
                    >
                      <AppColumn>
                        <AppText.FootnoteSmall
                          color={theme.colors.grey}
                          fontWeight={700}
                        >
                          {t("jobdetail.workplace.insight.benefits.title")}
                        </AppText.FootnoteSmall>
                        <SizedBox height={theme.spacing.elementPaddingSmall} />
                        <AppText.Caption>
                          {insight.bestThingAnswer}
                        </AppText.Caption>
                        <SizedBox height={theme.spacing.contentElementPadding} />
                        <AppText.FootnoteSmall
                          color={theme.colors.grey}
                          fontWeight={700}
                        >
                          {t("jobdetail.workplace.insight.disadvantages")}
                        </AppText.FootnoteSmall>
                        <SizedBox height={theme.spacing.elementPaddingSmall} />
                        <AppText.Caption>
                          {insight.improvementAnswer}{" "}
                        </AppText.Caption>
                        <SizedBox height={theme.spacing.contentElementPadding} />
                        <LightHorizontalDivider />
                        <SizedBox height={theme.spacing.elementPadding} />
                        <AppRow mainAxisAlignment={"space-between"}>
                          <InsightCardAverageRating
                            average={insight.average.score}
                          />
                          <AppText.Footnote>
                            {isoDate({
                              dateString: insight.createdAt,
                              removeTime: true,
                            })}
                          </AppText.Footnote>
                        </AppRow>
                      </AppColumn>
                    </div>
                  );
                })}
              </div>
            </div>
            <SizedBox height={isMobile && theme.spacing.elementPadding} />
            <div
              style={{
                padding: !isMobile && theme.spacing.contentElementPadding,
              }}
            >
              <PoweredByMedalla />
            </div>
          </AppColumn>
        )}
        <SizedBox height={isMobile && theme.spacing.elementPadding} />
        {((healthcareWorkplaceDetails?.adress_obj &&
          healthcareWorkplaceDetails["adress_obj"].length > 0) ||
          (healthcareWorkplaceDetails?.geoPoint &&
            healthcareWorkplaceDetails.geoPoint["_latitude"])) &&
          !isMobile && <LightHorizontalDivider />}
        {healthcareWorkplaceDetails?.adress_obj &&
          healthcareWorkplaceDetails["adress_obj"].length > 0 && (
            <AppColumn
              style={{
                paddingLeft: !isMobile && theme.spacing.contentElementPadding,
                paddingRight: !isMobile && theme.spacing.contentElementPadding,
                paddingTop: isMobile
                  ? theme.spacing.elementPadding
                  : theme.spacing.contentElementPadding,
              }}
            >
              <SizedBox height={isMobile && theme.spacing.elementPadding} />
              <AppText.BodyText
                margins={{ marginBottom: "0px" }}
                fontWeight={600}
              >
                {t("location")}
              </AppText.BodyText>
              <SizedBox height={theme.spacing.elementPaddingSmall} />
              <AppRow
                crossAxisAlignment={"center"}
                style={{
                  paddingBottom: theme.spacing.contentElementPadding,
                }}
              >
                <Icon name="map pin" />
                <SizedBox width={theme.spacing.elementPaddingSmall} />
                <AppText.Caption>
                  {healthcareWorkplaceDetails["adress_obj"]},{" "}
                  {healthcareWorkplaceDetails["kommun_obj"]}
                </AppText.Caption>
              </AppRow>
            </AppColumn>
          )}
        {healthcareWorkplaceDetails?.geoPoint &&
        healthcareWorkplaceDetails.geoPoint["_latitude"] ? (
          <div
            style={{
              width: "100%",
            }}
          >
            <WorkplaceMap
              workplace={healthcareWorkplaceDetails}
              isMobile={isMobile}
            />
          </div>
        ) : (
          <SizedBox height={theme.spacing.contentElementPadding} />
        )}
      </AppColumn>
    </div>
  );

  return (
    <div ref={workplaceRef}>
      <SizedBox height={theme.spacing.elementPaddingSmall} />

      {isMobile ? (
        workplaceSectionContent
      ) : (
        <Card
          fluid
          style={{
            borderRadius: theme.radius.default,
          }}
        >
          {workplaceSectionContent}
        </Card>
      )}
    </div>
  );
}

const WorkplaceMap = ({ workplace, isMobile }) => {
  const theme = useTheme();
  const posisition = {
    lat: workplace.geoPoint["_latitude"],
    lng: workplace.geoPoint["_longitude"],
  };

  const { isLoaded: isMapLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyDROtZmtOmKG9kb4xHzsA9Yr_YPc5OHwKk",
  });

  if (!isMapLoaded) {
    return (
      <Segment
        basic
        loading={true}
        style={{
          width: "100vw",
          height: "50vh",
        }}
      />
    );
  }

  const markers = [
    {
      id: 1,
      position: posisition,
      title: workplace["name_obj"],
    },
  ];

  return (
    <GoogleMap
      mapContainerStyle={{
        height: "50vh",
        borderBottomLeftRadius: !isMobile && theme.radius.default,
        borderBottomRightRadius: !isMobile && theme.radius.default,
      }}
      center={posisition}
      zoom={16}
      options={{
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: false,
      }}
    >
      {markers.map((marker) => (
        <Marker key={marker.id} position={marker.position} />
      ))}
    </GoogleMap>
  );
};

export default WorkplaceMap;

const emojiIconByValue = (value) => {
  if (value > 4) {
    return ASSETS.faceLol;
  } else if (value > 3) {
    return ASSETS.faceHappy;
  } else if (value > 2) {
    return ASSETS.faceNeutral;
  } else if (value > 1) {
    return ASSETS.faceSad;
  } else {
    return ASSETS.faceAngry;
  }
};

const InsightCardAverageRating = ({ average }) => {
  if (average === null || average === undefined) return null;

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <img
        src={emojiIconByValue(Math.round(average))}
        style={{
          height: "20px",
          marginRight: "8px",
          filter: "invert(0%) brightness(0%)",
        }}
      />
      <AppText.Footnote>{average.toFixed(2)}</AppText.Footnote>
    </div>
  );
};
